/**
 * Reviews Slider Section Component
 * Handles the display and interaction of product reviews in a slider
 */

// Constants for configuration
const CONFIG = {
  SELECTORS: {
    SLIDER: '.js-reviews-slider__slider',
    READ_MORE: '.js-reviews-slider__read-more',
    READ_LESS: '.js-reviews-slider__read-less',
    TEXT_CONTAINER: '.js-reviews-slider__text',
    TRUNCATED_TEXT: '[data-text="truncated"]',
    FULL_TEXT: '[data-text="full"]'
  },
  CLASSES: {
    LOADING: 'is-loading',
    LOADED: 'is-loaded',
    HIDDEN: 'is-hidden'
  },
  FLICKITY: {
    cellAlign: 'left',
    wrapAround: true,
    prevNextButtons: false,
    groupCells: true
  },
  RETRY: {
    MAX_ATTEMPTS: 10,
    DELAY: 100
  },
  RESIZE: {
    DEBOUNCE_DELAY: 200,
    HEIGHT_PADDING: 20
  }
};

// Utility functions
const utils = {
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  async waitForLibrary(libraryName, maxAttempts = CONFIG.RETRY.MAX_ATTEMPTS, delay = CONFIG.RETRY.DELAY) {
    return new Promise((resolve, reject) => {
      const checkLibrary = (attempts = 0) => {
        if (window[libraryName]) {
          resolve();
        } else if (attempts < maxAttempts) {
          setTimeout(() => checkLibrary(attempts + 1), delay);
        } else {
          reject(new Error(`${libraryName} library failed to load after max retries.`));
        }
      };
      checkLibrary();
    });
  }
};

// Slider Manager Class
class SliderManager {
  constructor(element) {
    this.element = element;
    this.state = {
      flickity: null,
      slider: null,
      resizeTimeout: null
    };
  }

  initialize() {
    this.state.slider = this.element.querySelector(CONFIG.SELECTORS.SLIDER);
    if (!this.isSliderReady()) return false;

    this.state.flickity = new window.Flickity(this.state.slider, CONFIG.FLICKITY);
    this.state.flickity.on('ready', () => this.adjustHeight());
    this.state.flickity.on('resize', () => this.adjustHeight());
    return true;
  }

  isSliderReady() {
    return this.state.slider && this.state.slider.children.length > 0;
  }

  adjustHeight() {
    if (!this.isSliderReady()) return;

    const maxHeight = Math.max(
      ...Array.from(this.state.slider.children).map(slide => slide.offsetHeight)
    );

    const newHeight = maxHeight + CONFIG.RESIZE.HEIGHT_PADDING;
    if (maxHeight > 0 && this.state.slider.style.height !== `${newHeight}px`) {
      this.state.slider.style.height = `${newHeight}px`;
    }
  }

  resize() {
    this.adjustHeight();
    this.state.flickity?.resize();
  }

  destroy() {
    if (this.state.flickity) {
      this.state.flickity.off('ready', this.adjustHeight);
      this.state.flickity.off('resize', this.adjustHeight);
      this.state.flickity.destroy();
      this.state.flickity = null;
    }
  }
}

// Text Manager Class
class TextManager {
  constructor(element) {
    this.element = element;
    this.state = {
      readMoreButtons: null,
      readLessButtons: null
    };
  }

  initialize() {
    this.state.readMoreButtons = this.element.querySelectorAll(CONFIG.SELECTORS.READ_MORE);
    this.state.readLessButtons = this.element.querySelectorAll(CONFIG.SELECTORS.READ_LESS);
    this.attachButtonListeners();
  }

  attachButtonListeners() {
    this.state.readMoreButtons.forEach(button => 
      button.addEventListener('click', this.handleReadMore.bind(this))
    );

    this.state.readLessButtons.forEach(button => 
      button.addEventListener('click', this.handleReadLess.bind(this))
    );
  }

  handleReadMore(event) {
    const textContainer = event.target.closest(CONFIG.SELECTORS.TEXT_CONTAINER);
    if (!textContainer) return;

    textContainer.querySelector(CONFIG.SELECTORS.TRUNCATED_TEXT).classList.add(CONFIG.CLASSES.HIDDEN);
    textContainer.querySelector(CONFIG.SELECTORS.FULL_TEXT).classList.remove(CONFIG.CLASSES.HIDDEN);
  }

  handleReadLess(event) {
    const textContainer = event.target.closest(CONFIG.SELECTORS.TEXT_CONTAINER);
    if (!textContainer) return;

    textContainer.querySelector(CONFIG.SELECTORS.FULL_TEXT).classList.add(CONFIG.CLASSES.HIDDEN);
    textContainer.querySelector(CONFIG.SELECTORS.TRUNCATED_TEXT).classList.remove(CONFIG.CLASSES.HIDDEN);
  }

  cleanup() {
    this.state.readMoreButtons?.forEach(button => 
      button?.removeEventListener('click', this.handleReadMore)
    );

    this.state.readLessButtons?.forEach(button => 
      button?.removeEventListener('click', this.handleReadLess)
    );
  }
}

// Main Component Class
class SectionReviewsSlider extends HTMLElement {
  constructor() {
    super();
    this.state = {
      pubSubToken: null
    };
    this.sliderManager = new SliderManager(this);
    this.textManager = new TextManager(this);
    this.handleResize = utils.debounce(this.handleResize.bind(this), CONFIG.RESIZE.DEBOUNCE_DELAY);
  }

  connectedCallback() {
    this.classList.add(CONFIG.CLASSES.LOADING);
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  async initialize() {
    try {
      await utils.waitForLibrary('Flickity');
      this.initializeComponents();
      this.attachEventListeners();
    } catch (error) {
      console.error('Failed to initialize slider:', error);
      this.classList.remove(CONFIG.CLASSES.LOADING);
    }
  }

  initializeComponents() {
    this.textManager.initialize();
    if (this.sliderManager.initialize()) {
      this.classList.remove(CONFIG.CLASSES.LOADING);
      this.classList.add(CONFIG.CLASSES.LOADED);
    }
  }

  attachEventListeners() {
    window.addEventListener('resize', this.handleResize);
    this.state.pubSubToken = window.PubSub?.subscribe('slider-resize', () => this.sliderManager.resize());
  }

  handleResize() {
    if (this.shouldReinitializeSlider()) {
      this.reinitializeSlider();
    }
  }

  shouldReinitializeSlider() {
    return this.sliderManager.state.flickity && 
           this.sliderManager.state.slider && 
           this.sliderManager.state.slider.offsetWidth !== this.sliderManager.state.flickity.size?.width;
  }

  reinitializeSlider() {
    this.sliderManager.destroy();
    this.sliderManager.initialize();
  }

  cleanup() {
    this.sliderManager.destroy();
    this.textManager.cleanup();
    window.removeEventListener('resize', this.handleResize);
    if (this.state.pubSubToken && window.PubSub) {
      window.PubSub.unsubscribe(this.state.pubSubToken);
    }
  }
}

// Register the custom element
if (!customElements.get('s-reviews-slider')) {
  customElements.define('s-reviews-slider', SectionReviewsSlider);
}